import React, { useEffect, useMemo, useState } from "react";
import { DataGridPremium, GridColDef, GridToolbar, useGridApiRef, GridRowSelectionModel, GRID_CHECKBOX_SELECTION_COL_DEF, GridColumnVisibilityModel } from "@mui/x-data-grid-premium";
import { Button, Chip, Tooltip, Select, Option, Modal, ModalDialog, Checkbox } from "@mui/joy";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import totalLocationData from "../../../../../../data/locationdata";
import CustomInput from "../../../etc/custominput";
import { tableUseStyle } from "../../../etc/tableusestyle";
import {
    API, toastOptions, errorText, EXCLUDED_LOCATIONS, Position,
    todayStr, mmdd, periodText, formatPhone, isActivePosition,
    badgeText, contractStateText, videoConsentText, accountText, monthsBetween,
    renderBadgeChip, renderContractStateChip, renderVideoConsentChip, openFileLink, ResidentCell, AccountCell
} from "./rosterutils";
import { TextCell, saveField, toastResult } from "./stafftodocommon";
import { goToStaffLeaveTodo } from "./navigation";
import styles from "./staffmanage.module.css";
import PositionEditModal from "./positioneditmodal";
import SendContractModal from "./sendcontractmodal";
import ContractHistoryModal from "./contracthistorymodal";
import PositionVarsModal from "./positionvarsmodal";
import RemindModal, { remindTargetsOf } from "./remindmodal";

//직원명부 — 한 줄 = 직무(staffPositions). 같은 사람 두 직무면 두 줄 + 겸직 태그
//필터는 클라이언트에서 (서버는 직무 전체를 내려줌). 계약서 발송·재알림·취소·이력·개인 변수·파일 열기가 여기서 시작
type StatusFilter = "active" | "left" | "all" | "hidden";   //hidden = 숨김 보기 (isManaged=0 만)
type BadgeFilter = "all" | "first" | "before10" | "expired" | "unsigned" | "leaving";
type TenureFilter = "all" | "1" | "2" | "3" | "12";   //근무 개월 수 이상

//"보이는 열 편집" 에서 끌 수 없는 열 (이름·편집)
const FIXED_COLUMNS = ["name", "edit"];

const CANCEL_MESSAGE = "관리자에 의해 취소되었습니다";

//구버전 서버 응답(isManaged 없음)이면 전부 관리 대상으로 취급
const normalize = (list: any[]): Position[] => (list || []).map((p: any) => ({ ...p, onboarding: p.onboarding || {}, offboarding: p.offboarding || {}, isManaged: p.isManaged === undefined ? 1 : p.isManaged }));

const StaffRoster: React.FC<any> = (props) => {

    const classes = tableUseStyle();
    const apiRef = useGridApiRef();

    const [positions, setPositions] = useState<Position[]>([]);
    const [parts, setParts] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [status, setStatus] = useState<StatusFilter>("active");
    const [branch, setBranch] = useState<string>("all");
    const [badgeFilter, setBadgeFilter] = useState<BadgeFilter>("all");
    const [partFilter, setPartFilter] = useState<string>("all");          //"all" | "none"(파트 없음) | partId
    const [tenureFilter, setTenureFilter] = useState<TenureFilter>("all");
    const [searchText, setSearchText] = useState<string>(props.initialSearch || "");

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [rowBusyId, setRowBusyId] = useState<number | null>(null);

    //보이는 열: 서버에 저장된 숨김 목록(모두 공통) → columnVisibilityModel. 툴바에서 개인이 임시로 바꿀 수는 있음
    const [hiddenSaved, setHiddenSaved] = useState<string[]>([]);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
    const [colModal, setColModal] = useState<{ open: boolean; hidden: string[]; saving: boolean }>({ open: false, hidden: [], saving: false });

    const [editModal, setEditModal] = useState<{ open: boolean; mode: "edit" | "create"; position: Position | null; userId: number; userName: string }>({ open: false, mode: "edit", position: null, userId: 0, userName: "" });
    const [sendOpen, setSendOpen] = useState(false);
    const [remindOpen, setRemindOpen] = useState(false);
    const [historyTarget, setHistoryTarget] = useState<{ userId: number; name: string } | null>(null);
    const [varsTarget, setVarsTarget] = useState<Position | null>(null);

    const today = todayStr();

    //CRM 지점 목록 (직무 지점 = CRM 호점, 대치는 2층/3층/6층 따로)
    const branchOptions = totalLocationData.filter((each) =>
        props.user && each.academy.includes(props.user.academy) && !EXCLUDED_LOCATIONS.includes(each.english));

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            const [r, p, c]: any[] = await Promise.all([getFetch(`${API}/roster`), getFetch(`${API}/parts`), getFetch(`${API}/roster/columns`)]);
            if (r.message === "success") {
                setPositions(normalize(r.positions));
            } else {
                toast.error(errorText(r, "직원명부를 불러오지 못했습니다"), toastOptions);
            }
            if (p.message === "success") {
                setParts(p.parts || []);
            }
            if (c && c.message === "success") {
                applyHidden(c.hidden || []);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const applyHidden = (hidden: string[]) => {
        setHiddenSaved(hidden);
        const model: GridColumnVisibilityModel = {};
        hidden.forEach((f) => { if (!FIXED_COLUMNS.includes(f)) model[f] = false; });
        setColumnVisibilityModel(model);
    };

    const saveHidden = async () => {
        try {
            setColModal((prev) => ({ ...prev, saving: true }));
            const result: any = await postFetch(`${API}/roster/columns`, { hidden: colModal.hidden });
            if (result.message === "success") {
                applyHidden(result.hidden || []);
                toast.success("보이는 열을 저장했습니다 (모두에게 적용)", toastOptions);
                setColModal({ open: false, hidden: [], saving: false });
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
                setColModal((prev) => ({ ...prev, saving: false }));
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
            setColModal((prev) => ({ ...prev, saving: false }));
        }
    };

    //변경 후 명부만 재조회. 사라진 행은 선택에서 제외
    const loadPositions = async () => {
        try {
            setLoading(true);
            const r: any = await getFetch(`${API}/roster`);
            if (r.message === "success") {
                const next: Position[] = normalize(r.positions);
                setPositions(next);
                const ids = new Set(next.map((x) => x.id));
                setSelectionModel((prev) => prev.filter((id) => ids.has(+id)));
            } else {
                toast.error(errorText(r, "직원명부를 불러오지 못했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    //클라이언트 필터
    const visibleRows = useMemo(() => {
        const text = searchText.trim();
        const isNumber = text.length > 0 && !isNaN(+text[0]);
        return positions.filter((p) => {
            const active = isActivePosition(p, today);
            //기본은 관리 대상만. "숨김 보기" 는 숨긴 사람만
            if (status === "hidden") {
                if (p.isManaged === 1) return false;
            } else if (p.isManaged !== 1) {
                return false;
            }
            if (status === "active" && !active) return false;
            if (status === "left" && active) return false;
            if (branch !== "all" && p.branchKey !== branch) return false;
            if (badgeFilter === "first" && !(p.badge && p.badge.type === "first")) return false;
            if (badgeFilter === "before10" && !(p.badge && p.badge.type === "before" && p.badge.days !== null && p.badge.days <= 10)) return false;
            if (badgeFilter === "expired" && !(p.badge && p.badge.type === "expired")) return false;
            if (badgeFilter === "unsigned" && p.contractState !== "pending") return false;
            if (badgeFilter === "leaving" && !(p.badge && p.badge.type === "leaving")) return false;
            if (partFilter === "none" ? !!p.partId : (partFilter !== "all" && String(p.partId || "") !== partFilter)) return false;
            if (tenureFilter !== "all") {
                const months = monthsBetween(p.startDate, p.endDate || today);
                if (months === null || months < +tenureFilter) return false;
            }
            if (!text) return true;
            if (isNumber) return String(p.phoneNumber || "").replace(/[^0-9]/g, "").includes(text.replace(/[^0-9]/g, ""));
            return String(p.name || "").includes(text);
        });
    }, [positions, status, branch, badgeFilter, partFilter, tenureFilter, searchText, today]);

    const selectedRows = useMemo(() => {
        const ids = new Set(selectionModel.map((id) => +id));
        return positions.filter((p) => ids.has(p.id));
    }, [selectionModel, positions]);
    const remindTargetCount = useMemo(() => remindTargetsOf(selectedRows).length, [selectedRows]);

    //===== 행 액션 =====

    const remind = async (row: Position) => {
        if (!row.latestDoc) return;
        if (!window.confirm(`${row.name}에게 서명 재알림을 보낼까요?${row.expired ? " (유효기간이 지나 7일 연장 후 보냅니다)" : ""}`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/remind`, { id: row.latestDoc.id });
            if (result.message === "success") {
                toast.success(`재알림 발송 (${result.remindCount}회${result.extended ? ", 유효기간 +7일" : ""})`, toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "재알림에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    const cancel = async (row: Position) => {
        if (!row.latestDoc) return;
        if (!window.confirm(`${row.name}의 계약서 서명 요청을 취소할까요? (취소 후 다시 발송할 수 있습니다)`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/documents/cancel`, { id: row.latestDoc.id, message: CANCEL_MESSAGE });
            if (result.message === "success") {
                toast.success("요청을 취소했습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "취소에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    //명부 관리 대상 켜기/끄기 — 직무·이력은 삭제하지 않음
    const setManaged = async (row: Position, managed: boolean) => {
        if (!window.confirm(managed ? `${row.name}을(를) 다시 직원명부에서 관리할까요?` : `${row.name}을(를) 직원명부에서 숨길까요? (직무·계약 이력은 지워지지 않고 "숨김 보기"에서 다시 켤 수 있습니다)`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/profiles/managed`, { userId: row.userId, managed });
            if (result.message === "success") {
                toast.success(managed ? "다시 관리 대상으로 켰습니다" : "명부에서 숨겼습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(errorText(result, "저장에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    //첫 계약 첨부 OCR 다시 읽기 (needsReview 건)
    const reocr = async (row: Position) => {
        if (!window.confirm(`${row.name}의 첫 계약서 첨부(신분증·통장사본)를 다시 읽을까요? 이미 있는 값은 덮어쓰지 않습니다.`)) return;
        try {
            setRowBusyId(row.id);
            const result: any = await postFetch(`${API}/profiles/reocr`, { userId: row.userId });
            if (result.message === "success") {
                const pf = result.profile || {};
                toast[pf.needsReview ? "error" : "success"](pf.needsReview ? `다시 읽었지만 확인 필요: ${pf.reviewMemo || ""}` : "주민번호·계좌를 읽어왔습니다", toastOptions);
                loadPositions();
            } else {
                toast.error(result.message === "noFirstContract" ? "서명완료된 첫 계약서가 없습니다" : errorText(result, "다시 읽기에 실패했습니다"), toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setRowBusyId(null);
        }
    };

    //비고 인라인 수정 (해제된 줄도 허용)
    const saveMemo = async (row: Position, value: string) => {
        const r = await saveField(row.id, "memo", value);
        if (toastResult(r, null, "저장에 실패했습니다")) loadPositions();
    };

    //엑셀 — DataGridPremium 내장 export (exceljs 는 x-data-grid-premium 의존성으로 이미 설치됨). 실패 시 CSV
    const exportExcel = async () => {
        const fileName = `직원명부_${today}`;
        try {
            await apiRef.current.exportDataAsExcel({ fileName });
        } catch (e) {
            console.log(e);
            apiRef.current.exportDataAsCsv({ fileName, utf8WithBom: true });
        }
    };

    //===== 컬럼 =====

    const columns: GridColDef[] = useMemo(() => [
        {
            field: "badge", headerName: "딱지", width: 120,
            valueGetter: (params: any) => badgeText(params.row.badge),
            renderCell: (params: any) => renderBadgeChip(params.row.badge)
        },
        { field: "name", headerName: "이름", width: 100, cellClassName: "boldcell" },
        { field: "branchLabel", headerName: "지점", width: 90 },
        { field: "partName", headerName: "파트", width: 170, valueGetter: (params: any) => params.row.partName || "" ,
            renderCell: (params: any) => params.row.partName ? params.row.partName : <span style={{ color: "#d32f2f" }}>파트 없음</span> },
        {
            field: "isMultiple", headerName: "겸직", width: 60,
            valueGetter: (params: any) => (params.row.isMultiple ? "겸직" : ""),
            renderCell: (params: any) => (params.row.isMultiple ? <Chip size="sm" variant="soft" color="primary">겸직</Chip> : "")
        },
        { field: "phoneNumber", headerName: "연락처", width: 120, valueFormatter: (params: any) => formatPhone(params.value) },
        {
            field: "residentNumber", headerName: "주민등록번호", width: 250,
            renderCell: (params: any) => <ResidentCell row={params.row} busy={rowBusyId === params.row.id} onReocr={reocr} />
        },
        {
            field: "bankAccount", headerName: "계좌", width: 240,
            valueGetter: (params: any) => accountText(params.row),
            renderCell: (params: any) => <AccountCell row={params.row} />
        },
        { field: "startDate", headerName: "입사일", width: 100, valueGetter: (params: any) => params.row.startDate || "" },
        { field: "endDate", headerName: "퇴사일", width: 100, valueGetter: (params: any) => params.row.endDate || "" },
        { field: "workDuration", headerName: "근무기간", width: 120 },
        { field: "employmentInsurance", headerName: "고보", width: 60, valueGetter: (params: any) => params.row.employmentInsurance || "미정" },
        {
            field: "videoConsentStatus", headerName: "영상동의", width: 130,
            valueGetter: (params: any) => videoConsentText(params.row.videoConsentStatus),
            renderCell: (params: any) => (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {renderVideoConsentChip(params.row.videoConsentStatus)}
                    <Button size="sm" variant="outlined" color="neutral" disabled={!params.row.videoConsentDocumentId}
                        onClick={() => openFileLink({ kind: "file", documentId: params.row.videoConsentDocumentId })}>보기</Button>
                </div>
            )
        },
        {
            field: "lastPeriod", headerName: "최근 계약기간", width: 130,
            valueGetter: (params: any) => (params.row.latestDoc ? periodText(params.row.latestDoc.periodStart, params.row.latestDoc.periodEnd) : "없음")
        },
        {
            field: "contractState", headerName: "계약 상태", width: 150,
            valueGetter: (params: any) => contractStateText(params.row),
            renderCell: (params: any) => renderContractStateChip(params.row)
        },
        {
            field: "expiresAt", headerName: "서명 유효기간", width: 120,
            valueGetter: (params: any) => (params.row.contractState === "pending" && params.row.latestDoc ? `${mmdd(params.row.latestDoc.expiresAt)}${params.row.expired ? " 만료됨" : ""}` : "-"),
            renderCell: (params: any) => {
                if (params.row.contractState !== "pending" || !params.row.latestDoc) return "-";
                return params.row.expired
                    ? <span style={{ color: "#d32f2f", fontFamily: "Apple_SB" }}>{mmdd(params.row.latestDoc.expiresAt)} 만료됨</span>
                    : <span>{mmdd(params.row.latestDoc.expiresAt)}</span>;
            }
        },
        {
            field: "remindCount", headerName: "재알림", width: 90,
            valueGetter: (params: any) => `${params.row.latestDoc ? params.row.latestDoc.remindCount : 0}회`,
            renderCell: (params: any) => {
                const log = params.row.latestDoc ? params.row.latestDoc.remindLog || [] : [];
                const title = log.length ? log.map((l: any) => `${mmdd(l.at)} ${l.byName || ""}${l.extended ? " (+7일)" : ""}`).join("\n") : "재알림 기록 없음";
                return <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{title}</div>} placement="top"><span>{params.row.latestDoc ? params.row.latestDoc.remindCount : 0}회</span></Tooltip>;
            }
        },
        {
            field: "lastSent", headerName: "최근 발송", width: 130,
            valueGetter: (params: any) => (params.row.latestDoc ? `${params.row.latestDoc.sentByName} · ${mmdd(params.row.latestDoc.sentAt)}` : "-")
        },
        {
            field: "contractActions", headerName: "계약서", width: 210, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => {
                const row: Position = params.row;
                if (row.contractState === "pending" && row.latestDoc) {
                    return (
                        <div style={{ display: "flex", gap: "6px" }}>
                            <Button size="sm" variant="outlined" color={row.expired ? "warning" : "primary"} loading={rowBusyId === row.id} onClick={() => remind(row)}>
                                {row.expired ? "재알림 (+7일 연장)" : "재알림"}
                            </Button>
                            <Button size="sm" variant="outlined" color="danger" disabled={rowBusyId === row.id} onClick={() => cancel(row)}>취소</Button>
                        </div>
                    );
                }
                if (row.contractState === "completed" && row.latestDoc) {
                    return <Button size="sm" variant="outlined" color="neutral" onClick={() => openFileLink({ kind: "file", documentId: row.latestDoc!.documentId })}>PDF</Button>;
                }
                return "-";
            }
        },
        {
            field: "history", headerName: "이력", width: 90, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => <Button size="sm" variant="plain" onClick={() => setHistoryTarget({ userId: params.row.userId, name: params.row.name })}>전체보기</Button>
        },
        {
            field: "vars", headerName: "변수", width: 70, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => <Button size="sm" variant="plain" disabled={!params.row.partId} onClick={() => setVarsTarget(params.row)}>확인</Button>
        },
        {
            field: "acquisitionReport", headerName: "취득신고", width: 170,
            //신규직원 TODO 와 같은 값 (onboarding.acquisitionInput / acquisitionDone)
            valueGetter: (params: any) => {
                const ob = params.row.onboarding || {};
                const i = ob.acquisitionInput ? `입력 ${mmdd(ob.acquisitionInput.value)}` : "";
                const d = ob.acquisitionDone ? `이행 ${mmdd(ob.acquisitionDone.value)}` : "";
                return [i, d].filter(Boolean).join(" · ");
            }
        },
        {
            field: "memo", headerName: "비고", width: 170, valueGetter: (params: any) => params.row.memo || "",
            renderCell: (params: any) => <TextCell value={params.row.memo || ""} width={160} onCommit={(v) => saveMemo(params.row, v)} />
        },
        {
            field: "edit", headerName: "편집", width: 340, sortable: false, filterable: false, disableExport: true,
            renderCell: (params: any) => (
                <div style={{ display: "flex", gap: "6px" }}>
                    <Button size="sm" variant="outlined" color="neutral" onClick={() => setEditModal({ open: true, mode: "edit", position: params.row, userId: params.row.userId, userName: params.row.name })}>줄 편집</Button>
                    {isActivePosition(params.row, today) && props.history &&
                        <Button size="sm" variant="plain" color="warning" onClick={() => goToStaffLeaveTodo(props.history, String(params.row.name || ""))}>퇴사TODO</Button>}
                    <Button size="sm" variant="plain" color="neutral" onClick={() => setEditModal({ open: true, mode: "create", position: null, userId: params.row.userId, userName: params.row.name })}>파트 추가</Button>
                    {params.row.isManaged === 1
                        ? <Button size="sm" variant="plain" color="danger" loading={rowBusyId === params.row.id} onClick={() => setManaged(params.row, false)}>숨기기</Button>
                        : <Button size="sm" variant="soft" color="primary" loading={rowBusyId === params.row.id} onClick={() => setManaged(params.row, true)}>다시 관리</Button>}
                </div>
            )
        }
    ], [rowBusyId, today]);

    const toggleSx = { "& .MuiToggleButton-root": { padding: "4px 10px", fontSize: "12px" } };

    return (
        <div>

            {/* 필터 행 */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", marginBottom: "12px" }}>
                <ToggleButtonGroup color="primary" size="small" exclusive value={status} sx={toggleSx} onChange={(e, v) => { if (v) setStatus(v); }}>
                    <ToggleButton value="active">재직</ToggleButton>
                    <ToggleButton value="left">퇴사</ToggleButton>
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="hidden">숨김 보기</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={branch} sx={toggleSx} onChange={(e, v) => { if (v) setBranch(v); }}>
                    <ToggleButton value="all">전체</ToggleButton>
                    {branchOptions.map((b) => (
                        <ToggleButton value={b.english} key={b.english}>{b.korean}</ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={badgeFilter} sx={toggleSx} onChange={(e, v) => { if (v) setBadgeFilter(v); }}>
                    <ToggleButton value="all">전체</ToggleButton>
                    <ToggleButton value="first">첫 계약 전</ToggleButton>
                    <ToggleButton value="before10">계약 만료 10일 전</ToggleButton>
                    <ToggleButton value="expired">계약 만료</ToggleButton>
                    <ToggleButton value="unsigned">미서명</ToggleButton>
                    <ToggleButton value="leaving">퇴사 예정</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={tenureFilter} sx={toggleSx} onChange={(e, v) => { if (v) setTenureFilter(v); }}>
                    <ToggleButton value="all">근무기간 전체</ToggleButton>
                    <ToggleButton value="1">1개월 이상</ToggleButton>
                    <ToggleButton value="2">2개월 이상</ToggleButton>
                    <ToggleButton value="3">3개월 이상</ToggleButton>
                    <ToggleButton value="12">1년 이상</ToggleButton>
                </ToggleButtonGroup>
                <Select size="sm" value={partFilter} onChange={(e: any, v: any) => { if (v !== null) setPartFilter(v); }} sx={{ minWidth: "190px", fontSize: "12px" }}>
                    <Option value="all">파트 전체</Option>
                    <Option value="none">파트 없음</Option>
                    {parts.map((p) => <Option key={p.id} value={String(p.id)}>{p.name}</Option>)}
                </Select>
                <div style={{ marginLeft: "auto" }}>
                    <CustomInput value={searchText} onChange={(e: any) => setSearchText(e.target.value)} placeholder="이름 or 전화번호" />
                </div>
            </div>

            {/* 상단 버튼 */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                <Button size="sm" variant="solid" disabled={selectionModel.length === 0} onClick={() => setSendOpen(true)}>
                    체크한 직원 계약서 일괄 발송 ({selectionModel.length}건)
                </Button>
                <Button size="sm" variant="outlined" color="primary" disabled={remindTargetCount === 0} onClick={() => setRemindOpen(true)}>
                    체크한 직원 재알림 보내기 ({remindTargetCount}건)
                </Button>
                <Button size="sm" variant="outlined" color="neutral" onClick={() => setColModal({ open: true, hidden: [...hiddenSaved], saving: false })}>보이는 열 편집</Button>
                <Button size="sm" variant="outlined" color="neutral" onClick={exportExcel}>엑셀 다운로드</Button>
                <div style={{ marginLeft: "auto", fontFamily: "Apple_R", fontSize: "12px", color: "#9ca3af" }}>
                    표시 {visibleRows.length}줄 / 전체 {positions.length}줄
                </div>
            </div>

            <div className={classes.root} style={{ height: "680px", width: "100%", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    apiRef={apiRef}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={(m) => setSelectionModel(m)}
                    isRowSelectable={(params) => isActivePosition(params.row, today)}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(m) => setColumnVisibilityModel(m)}
                    initialState={{ pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, "badge", "name"] } }}
                    slots={{ toolbar: GridToolbar }}
                    getRowHeight={() => 44}
                    sx={{ "& .boldcell": { fontFamily: "Apple_SB" } }}
                />
            </div>

            <div style={{ marginTop: "12px", padding: "12px 16px", background: "#fffbea", border: "1px solid #f5e6a8", borderRadius: "8px", fontFamily: "Apple_R", fontSize: "12px", color: "#6b7280", lineHeight: 1.7 }}>
                <b>읽는 법</b> · 직원은 여기서 추가하지 않음 — 전체학생정보에서 직원 줄 선택 → "신규TODO" 를 누르면 명부에 자동 등록 · 한 사람이 두 파트를 하면 줄이 두 개(겸직 표시) · 계약서는 줄(파트)마다 따로 나감 ·
                <b> 계약 상태</b>는 유캔싸인에서 자동으로 들어옴 · <b>딱지</b> = 첫계약 전 / 만료 N일 전 / 만료 경과 N일 (지난 계약 종료일 기준) / 퇴사 N일 전 (퇴사일까지 계약이 덮인 줄) · <b>서명 유효기간</b> 지나면 재알림 눌렀을 때 자동으로 7일 연장하고 보냄 ·
                <b> 미서명</b>이 남아 있으면 그 줄은 새 계약서를 못 보내고 <b>재알림</b>만 됨(체크해서 여러 명에게 한 번에 가능) · 주민번호·계좌는 계약서 첨부(신분증·통장사본)에서 자동으로 읽어오고(한 번 읽으면 다시 안 읽음) "사진" 눌러 원본 확인, 고치려면 신규직원 TODO의 연필 · <b>보이는 열 편집</b>에서 저장한 열 구성은 모두에게 적용됨 · <b>고보</b>는 급여 페이지 고용보험과 연동
            </div>

            <Modal open={colModal.open} onClose={() => setColModal((prev) => ({ ...prev, open: false }))}>
                <ModalDialog sx={{ width: "560px", maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }}>
                    <div className={styles.modalTitle}>보이는 열 편집 (저장하면 모두에게 적용)</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "6px 12px", margin: "6px 0 4px" }}>
                        {columns.filter((c) => !FIXED_COLUMNS.includes(c.field)).map((c) => (
                            <Checkbox key={c.field} size="sm" label={c.headerName || c.field}
                                checked={!colModal.hidden.includes(c.field)}
                                onChange={(e) => setColModal((prev) => ({ ...prev, hidden: e.target.checked ? prev.hidden.filter((f) => f !== c.field) : [...prev.hidden, c.field] }))} />
                        ))}
                    </div>
                    <div className={styles.modalButtons}>
                        <div className={styles.hint}>이름·편집 열은 항상 보임 · 엑셀 다운로드에도 보이는 열만 나감</div>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <Button variant="plain" color="neutral" onClick={() => setColModal((prev) => ({ ...prev, hidden: [] }))} disabled={colModal.saving}>기본값 복원</Button>
                            <Button variant="plain" color="neutral" onClick={() => setColModal((prev) => ({ ...prev, open: false }))} disabled={colModal.saving}>닫기</Button>
                            <Button variant="solid" onClick={saveHidden} loading={colModal.saving}>저장 (모두에게 적용)</Button>
                        </div>
                    </div>
                </ModalDialog>
            </Modal>

            <PositionEditModal
                open={editModal.open}
                mode={editModal.mode}
                position={editModal.position}
                userId={editModal.userId}
                userName={editModal.userName}
                branchOptions={branchOptions}
                parts={parts}
                onClose={() => setEditModal((prev) => ({ ...prev, open: false }))}
                onSaved={loadPositions}
            />

            <SendContractModal
                open={sendOpen}
                positionIds={selectionModel.map((id) => +id)}
                user={props.user}
                onClose={(sent: boolean) => { setSendOpen(false); if (sent) { setSelectionModel([]); loadPositions(); } }}
            />

            <RemindModal
                open={remindOpen}
                rows={selectedRows}
                onClose={(sent: boolean) => { setRemindOpen(false); if (sent) loadPositions(); }}
            />

            <ContractHistoryModal
                open={!!historyTarget}
                userId={historyTarget ? historyTarget.userId : 0}
                userName={historyTarget ? historyTarget.name : ""}
                onClose={() => setHistoryTarget(null)}
                onChanged={loadPositions}
            />

            <PositionVarsModal
                open={!!varsTarget}
                position={varsTarget}
                onClose={() => setVarsTarget(null)}
                onSaved={loadPositions}
            />

        </div>
    );
};

export default StaffRoster;
